import { useTranslations } from '@aikoa/i18n/react'
import { useState } from 'react'
import { View } from 'react-native'
import { Label } from '~/components/typography/Content'
import { type ButtonSize, type ButtonVariant } from '~/components/ui/button'
import { InlineEditSheet } from '~/components/ui/InlineEditSheet'
import { PriceInput } from '~/components/ui/PriceInput'
import { useAddAssetPrice } from '~/data/assets/useAssets'

type AddPriceInlineProps = {
  currentPrice?: number
  assetId: string
  isProspect?: boolean
  onSuccess?: () => void
  className?: string
  triggerClassName?: string
  triggerVariant?: ButtonVariant
  triggerSize?: ButtonSize
  triggerLabel?: string
}

export function AddPriceInline({
  currentPrice,
  assetId,
  isProspect = true,
  onSuccess,
  className,
  triggerClassName,
  triggerVariant = 'outline',
  triggerSize = 'default',
  triggerLabel,
}: AddPriceInlineProps) {
  const { t } = useTranslations(['property', 'common'])
  const [price, setPrice] = useState<number | undefined>(currentPrice)
  const { mutate: addPrice, isPending } = useAddAssetPrice(assetId)

  const defaultTriggerLabel = t(($) => $.actions.addPrice)

  const handleSave = () => {
    if (!price) return

    addPrice(
      {
        price,
        priceType: isProspect ? 'estimated_price' : 'asking_price',
      },
      {
        onSuccess: () => {
          setPrice(undefined)
          onSuccess?.()
        },
      }
    )
  }

  const handleCancel = () => {
    setPrice(undefined)
  }

  return (
    <InlineEditSheet
      modalKey={`add-price-${assetId}`}
      title={isProspect ? t(($) => $.price.addEstimatedPrice) : t(($) => $.price.addAskingPrice)}
      triggerLabel={triggerLabel ?? defaultTriggerLabel}
      onSave={handleSave}
      onCancel={handleCancel}
      isSaving={isPending}
      isDisabled={isPending}
      className={className}
      triggerClassName={triggerClassName}
      triggerVariant={triggerVariant}
      triggerSize={triggerSize}>
      <View className="gap-2">
        <Label className="text-center">
          {isProspect ? t(($) => $.price.estimatedPrice) : t(($) => $.price.askingPrice)}
        </Label>
        <PriceInput value={price} onChange={setPrice} placeholder="0" variant="box" />
      </View>
    </InlineEditSheet>
  )
}
